<script setup lang="ts">
  // Product sales stats migrated to the V2 design system: entity-mode table
  // (one row per product, with thumbnail) + top-15 bar chart.
  import { computed, getCurrentInstance, ref } from 'vue';
  import StatsService from '@/api/services/stats.service';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import V2ReportPage from '@/components/v2/stats/V2ReportPage.vue';
  import V2StatsDateRange from '@/components/v2/stats/V2StatsDateRange.vue';
  import V2StatsScopeNote from '@/components/v2/stats/V2StatsScopeNote.vue';
  import V2StatsSeriesReport from '@/components/v2/stats/V2StatsSeriesReport.vue';
  import { IV2StatsTableRow } from '@/components/v2/stats/v2-stats-table.types';
  import { useStatsSeriesDisplay } from '@/composition/use-stats-series-display';
  import { useUi } from '@/composition/use-ui';
  import { IStatsDateRange } from '@/domain/stats/stats-date-range';
  import { IEntityStats } from '@/interfaces/IEntityStats';
  import { IItem } from '@/interfaces/IItem';
  import { Globals } from '@/modules/Globals';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const statsService = new StatsService();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const currencySymbol = computed(() => restaurantStore.currency?.symbol ?? '');

  const isWhatsappMode = computed(
    () => restaurantStore.selectedBranch?.ordersMode === 'wa'
  );

  const range = ref<IStatsDateRange | null>(null);

  function onRangeChange(value: IStatsDateRange) {
    range.value = value;
    load();
  }

  const statsData = ref<IEntityStats<IItem> | null>(null);
  const loading = ref(false);

  const SERIES_LABEL_KEYS: Record<string, string> = {
    sales: 'v2Stats.series.unitsSold',
    income: 'v2Stats.series.totalIncome',
    average: 'v2Stats.series.avgPerUnit',
  };

  const SERIES_COLORS = ['#45a5f5', '#30d565', '#f5e938'];

  let requestGeneration = 0;

  async function load() {
    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    if (!restaurant || !branch || !range.value || isWhatsappMode.value) return;

    const generation = ++requestGeneration;
    loading.value = true;
    try {
      const stats = await statsService.getProductsStats(
        restaurant.id,
        branch.id,
        range.value.from,
        range.value.to,
        range.value.calendar
      );
      if (generation !== requestGeneration) return;

      stats.labels = stats.groups.length
        ? stats.groups[0].stats.map(
            (stat) =>
              (stat.id != null ? stats.entities[stat.id]?.name : null) ?? ''
          )
        : [];
      statsData.value = stats;
    } catch (error) {
      if (generation !== requestGeneration) return;
      statsData.value = null;
      ui.serverError(error, i18nT('v2Stats.products.loadError'));
    } finally {
      if (generation === requestGeneration) loading.value = false;
    }
  }

  const displayStats = useStatsSeriesDisplay({
    stats: statsData,
    labelKeys: SERIES_LABEL_KEYS,
    colors: SERIES_COLORS,
    currencySymbol,
  });

  function productThumb(row: IV2StatsTableRow): string | undefined {
    const entityId = row.entityId as number | undefined;
    if (entityId == null || !statsData.value) return undefined;
    const picture = statsData.value.entities[entityId]?.picture;
    if (!picture) return undefined;
    const parts = picture.split('.');
    return `${Globals.uploadsPath}/${parts[0]}_thumb.${parts[1]}`;
  }
</script>

<template>
  <V2ReportPage
    :title="String($t('v2Stats.products.title'))"
    :subtitle="restaurantStore.selectedBranch?.name"
  >
    <template #range>
      <V2StatsDateRange
        v-if="restaurantStore.restaurant && restaurantStore.selectedBranch"
        :restaurant-id="restaurantStore.restaurant.id"
        :branch-id="restaurantStore.selectedBranch.id"
        storage-key="products"
        @change="onRangeChange"
      />
    </template>

    <template #note>
      <V2StatsScopeNote
        v-if="!isWhatsappMode"
        storage-key="products"
        :text="String($t('statsNote.products'))"
      />
    </template>

    <div v-if="isWhatsappMode" class="wa-guard">
      <PosUiEmptyState
        icon="whatsapp"
        :title="String($t('v2Stats.waModeTitle'))"
        :description="String($t('v2Stats.waModeText'))"
      />
    </div>

    <V2StatsSeriesReport
      v-else
      :stats="displayStats"
      :loading="loading"
      chart-type="bar"
      :chart-title="String($t('v2Stats.products.chartTitle'))"
      :chart-limit="15"
      :currency-symbol="currencySymbol"
      export-filename="products-stats"
      :export-sheet="String($t('v2Stats.products.title'))"
      :first-column-label="String($t('v2Stats.colProduct'))"
      :default-sort="{ key: 'sales', desc: true }"
    >
      <template #first-cell="{ row }">
        <span class="product-cell">
          <img
            v-if="productThumb(row)"
            class="product-picture"
            :src="productThumb(row)"
            alt=""
          />
          <span class="product-name">{{ String(row.label ?? '') }}</span>
        </span>
      </template>
    </V2StatsSeriesReport>
  </V2ReportPage>
</template>

<style scoped lang="scss">
  .wa-guard {
    @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
  }

  .product-cell {
    @apply tw-inline-flex tw-min-w-0 tw-items-center;

    .product-picture {
      @apply tw-mr-2 tw-h-9 tw-w-9 tw-shrink-0 tw-rounded-lg tw-object-cover;
    }

    .product-name {
      @apply tw-font-medium tw-text-neutral-900;

      // Inside the capped mobile label column: clamp instead of overflowing.
      @media (max-width: 767.98px) {
        @apply tw-min-w-0 tw-overflow-hidden;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        word-break: break-word; // fallback for Chrome <80
        overflow-wrap: anywhere;
      }
    }
  }
</style>
